/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {GlobalSearchCriteriaConfiguration} from './configuration/global-search-criteria-configuration';
import {GlobalSearchCriteriaService} from './services/global-search-criteria.service';
import {StatefulComponent} from '../stateful/stateful.component';
import {GlobalSearchCriteriaState} from './state/global-search-criteria.state';
import {
    Subscription,
    timer
} from 'rxjs';
import {QueryChipModel} from './query-chips-area/models/query-chip.model';
import {SearchCriteriaInputTypeEnum} from './configuration/search-criteria-input-type.enum';
import {fastDeepCopy} from '../utils/deep-copy';
import {GlobalSearchCriteriaModel} from './models/global-search-criteria.model';

@Component({
    selector: 'app-global-search-criteria',
    templateUrl: './global-search-criteria.component.html',
    styleUrls: ['./global-search-criteria.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GlobalSearchCriteriaComponent extends StatefulComponent<GlobalSearchCriteriaState> implements OnInit, OnDestroy {
    @Input() globalSearchCriteriaConfiguration: GlobalSearchCriteriaConfiguration;
    @Input() globalSearchCriteriaModel: GlobalSearchCriteriaModel;
    @Input() resetGlobalSearchCriteria: EventEmitter<void>;

    @Output() globalSearchCriteria = new EventEmitter<QueryChipModel[]>();
    @ViewChild('chipsArea', {read: ElementRef, static: false}) chipsArea: ElementRef;

    dropdownSelectionChange = new EventEmitter<void>();
    inputCriteriaChange = new EventEmitter<void>();

    public readonly queryChipsAreaClearTooltipMessage = 'Remove all selected criteria';
    public readonly globalClearFiltersTooltipMessage = 'Clear all filters and refresh the list';
    private readonly hiddenChipCounterId = 'hiddenChipCounterId';

    private subscriptions = new Subscription();

    constructor(private globalSearchCriteriaService: GlobalSearchCriteriaService) {
        super();
    }

    get getSelectedSearchCriterionFilterInputType(): SearchCriteriaInputTypeEnum {
        return this.globalSearchCriteriaService.getSelectedSearchCriterionFilter()?.criterionFilterInputType;
    }

    get disabledExpandButton(): boolean {
        this.changeChipsAfterSubmit();
        return !this.state.isExpandChipsButtonActive;
    }

    ngOnInit(): void {
        this.globalSearchCriteriaService.setDropdownDataBySearchCriteriaCode(this.globalSearchCriteriaConfiguration.criteriaContexts);
        this.storeState({
            isClearFiltersButtonActive: false,
            isExpandChipsButtonActive: false,
            retractChipsAreaIconUp: false,
            queryCriteriaChips: []
        });

        this.resetGlobalSearchCriteria?.subscribe(() => {
            this.clearDropdownSection();
            this.clearFilterInputSection();
            this.clearQueryChipsAreaSection();
        });

        this.globalSearchCriteriaService.queryChipsAreaWidth.subscribe(() => {
            this.handleQueryChipsAreaWidthChange();
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    emitDropdownIsOpen(isOpen: boolean): void {
        this.storeState({
            retractChipsAreaIconUp: isOpen || this.globalSearchCriteriaService.hasSelectedFilter()
        });
    }

    showCriteriaFiltersChipsInQueryArea(): boolean {
        return this.state.retractChipsAreaIconUp
                || this.globalSearchCriteriaService.hasSelectedFilter()
                || (this.state.isExpandChipsButtonActive && !this.state.retractChipsAreaIconUp);
    }

    dropdownSearchCriteriaResetHandler(): void {
        this.clearInputCriteria();
    }

    inputCriteriaResetHandler(): void {
        this.clearDropdownCriteria();
    }

    submitSearchCriteria(): void {
        this.storeState({
            isClearFiltersButtonActive: true,
            isExpandChipsButtonActive: true,
            retractChipsAreaIconUp: false
        });
        this.clearDropdownSection();
        this.clearFilterInputSection();

        const criteriaFiltersInQuery = this.globalSearchCriteriaService.getCriteriaFiltersInQuery();
        this.globalSearchCriteria.emit(criteriaFiltersInQuery);

        this.storeState({queryCriteriaChips: fastDeepCopy(criteriaFiltersInQuery)});
    };

    globalSearchCriteriaClearHandler(): void {
        this.clearDropdownSection();
        this.clearFilterInputSection();
        this.clearQueryChipsAreaSection();
        this.storeState({
            isClearFiltersButtonActive: false
        });
        this.globalSearchCriteria.emit(null);
    }

    hasCriteriaFiltersChipsInQueryArea(): boolean {
        return this.globalSearchCriteriaService.hasQueryCriteriaFilters();
    }

    hasClearFiltersButton(): boolean {
        return this.state.isClearFiltersButtonActive;
    }

    clearCriteriaFiltersChips(): void {
        this.clearQueryChipsAreaSection();
        this.globalSearchCriteriaService.removeAllCriteriaFiltersFromQuery(true);
    }

    expandCriteriaFiltersChips(): void {
        this.storeState({retractChipsAreaIconUp: !this.state.retractChipsAreaIconUp});
    }

    getSelectedSearchCriterionFilterName(): string {
        return this.globalSearchCriteriaService.getSelectedSearchCriterionName();
    }

    hideGlobalSearchFilterInput(): boolean {
        return this.state.isExpandChipsButtonActive
                && this.globalSearchCriteriaService.hasQueryCriteriaFilters()
                && !this.state.retractChipsAreaIconUp;
    }

    openChipsArea(openChipsArea: boolean): void {
        this.storeState({retractChipsAreaIconUp: openChipsArea});
    }

    private clearInputCriteria(): void {
        this.inputCriteriaChange.emit();
    }

    private clearDropdownCriteria(): void {
        this.dropdownSelectionChange.emit();
    }

    private clearDropdownSection(): void {
        this.globalSearchCriteriaService.removeSelectedCriterion();
        this.clearDropdownCriteria();
    }

    private clearFilterInputSection(): void {
        this.clearInputCriteria();
    }

    private clearQueryChipsAreaSection(): void {
        this.globalSearchCriteriaService.removeAllCriteriaFiltersFromQuery(true);
        this.storeState({isExpandChipsButtonActive: false, retractChipsAreaIconUp: false});
    }

    private changeChipsAfterSubmit(): void {
        const value = fastDeepCopy(this.globalSearchCriteriaService.getCriteriaFiltersInQuery());

        if (this.state.isExpandChipsButtonActive
                && JSON.stringify(value) !== JSON.stringify(this.state.queryCriteriaChips)) {
            this.state.isExpandChipsButtonActive = false;
        }
    }

    private handleQueryChipsAreaWidthChange(): void {
        document.getElementById(this.hiddenChipCounterId)
                ?.remove();

        const queryChips: HTMLCollectionOf<Element> = document.getElementsByClassName('query-chip');
        let firstChipOffsetTop = 0;
        let hiddenChipsCounter = 0;
        let lastHiddenChipIndex = 0;
        const dynamicQueryChipCounterText = 'dynamicQueryChipCounterText';

        if (!this.state.retractChipsAreaIconUp) {
            Array.prototype.forEach?.call(queryChips, (el: HTMLElement, index: number) => {
                // Assign a offsetTop to the first chip (first line)
                if (index === 0) {
                    firstChipOffsetTop = el.offsetTop;
                }
                // Place a dynamic counter in front of the current chip index
                if (queryChips.length - 1 > index) {
                    document.getElementsByClassName('query-chip')
                            .item(lastHiddenChipIndex)
                            .append(this.generateCountText(1, dynamicQueryChipCounterText));
                }
                // Chips below first line should be hidden and counted
                if (el.offsetTop === firstChipOffsetTop) {
                    // Save the index of the last chip on the first line to delegate the counter
                    lastHiddenChipIndex = index;
                } else if (el.offsetTop > firstChipOffsetTop) {
                    hiddenChipsCounter++;
                }

                // On the end of each iteration, remove the dynamic counter that was placed in front of the current chip index.
                document.getElementById(dynamicQueryChipCounterText)
                        ?.remove();
            });

            const chipDelegatedWithCounter = document.getElementsByClassName('query-chip')
                    .item(lastHiddenChipIndex);
            if (chipDelegatedWithCounter && hiddenChipsCounter > 0) {
                chipDelegatedWithCounter.append(this.generateCountText(hiddenChipsCounter, this.hiddenChipCounterId));
            }
        }
    }

    private generateCountText(hiddenElementsCount: number, elementId: string): Element {
        const countChipsElement = document.createElement('p');
        countChipsElement.id = elementId;
        countChipsElement.style.background = '#F6F6F6';
        countChipsElement.style.padding = '5px 4px';
        countChipsElement.style.fontSize = '12px';
        countChipsElement.style.color = '#666666';
        countChipsElement.style.fontWeight = '700';

        const countChipsText = document.createTextNode(`+ ${hiddenElementsCount}`);
        countChipsElement.appendChild(countChipsText);
        return countChipsElement;
    }
}
